import * as Sentry from '@sentry/react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';

interface SectionErrorBoundaryProps {
  /** Bold heading for the contained fallback card. */
  title: string;
  /** Muted explanation shown under the title. */
  description: string;
  /** Label for the retry button. */
  retryLabel: string;
  /**
   * Runs alongside the boundary reset when the user clicks retry (and when
   * `resetKeys` change). Use it to invalidate the queries that feed the section
   * so the remount refetches fresh data instead of re-reading the cached payload
   * that threw — without it, retry would re-mount onto the same bad data.
   */
  onReset?: () => void;
  /** Reset the boundary automatically when any of these values change (e.g. the entity id). */
  resetKeys?: unknown[];
  /** Automation hook: fallback card id; title and retry button derive from it. */
  testId?: string;
  children: ReactNode;
}

/**
 * Component-scoped error boundary: contains a render throw to this subtree and
 * shows a compact, recoverable fallback card, instead of letting the error
 * bubble to the route boundary (which replaces the entire page). Still reports
 * to Sentry so contained crashes stay visible in telemetry.
 */
export function SectionErrorBoundary({
  title,
  description,
  retryLabel,
  onReset,
  resetKeys = [],
  testId = 'section-error',
  children,
}: SectionErrorBoundaryProps) {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      onReset={() => {
        onReset?.();
      }}
      onError={(error, info) => {
        Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
      }}
      // `fallbackRender` (not `FallbackComponent`) keeps this an inline render
      // prop rather than a nested component, sidestepping the remount-on-every-
      // parent-render that an inline component type would cause.
      fallbackRender={({ resetErrorBoundary }) => (
        <Card
          className="flex flex-col items-center gap-4 p-8 text-center sm:p-10"
          data-testid={testId}
        >
          <div className="bg-destructive/10 text-destructive flex h-14 w-14 items-center justify-center rounded-full">
            <AlertTriangle aria-hidden="true" className="h-7 w-7" />
          </div>
          <div className="space-y-1">
            <h2 className="text-foreground text-lg font-semibold" data-testid={`${testId}-title`}>
              {title}
            </h2>
            <p className="text-muted-foreground max-w-md text-sm">{description}</p>
          </div>
          <Button
            type="button"
            onClick={resetErrorBoundary}
            data-testid={`${testId}-retry-button`}
            className="mt-2 cursor-pointer gap-1.5"
          >
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            {retryLabel}
          </Button>
        </Card>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
